import { log } from './logger';

/**
 * Migrations here are applied by hand in the Supabase dashboard, so the app is
 * sometimes deployed before its SQL has been run. PostgREST then rejects the
 * whole write with PGRST204 ("Could not find the 'x' column"), which used to
 * block every save.
 *
 * Instead, writes drop the unknown column and retry, and report it here so the
 * UI can say what was not stored. The column is remembered so the same warning
 * is not repeated for every save in a session.
 */
const reported = new Set<string>();
const listeners = new Set<(column: string) => void>();

/** Column name from a PostgREST PGRST204 error, if that is what this is. */
export function getMissingColumn(error: unknown): string | null {
  const message = error && typeof error === 'object' && 'message' in error
    ? String((error as { message: unknown }).message)
    : String(error ?? '');
  const match = message.match(/could not find the '([^']+)' column/i);
  return match ? match[1] : null;
}

export function reportMissingColumn(column: string): void {
  log.warn(
    `[pendingSchema] Column "${column}" does not exist yet — saved without it. ` +
    'Apply the pending SQL update in Supabase.'
  );
  if (reported.has(column)) return;
  reported.add(column);
  listeners.forEach((fn) => fn(column));
}

export function subscribeMissingColumn(fn: (column: string) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Runs a write, and if the database is missing a column the payload carries,
 * removes it and tries again. Returns the result plus the dropped columns.
 */
export async function withMissingColumnRetry<T>(
  payload: Record<string, unknown>,
  run: (payload: Record<string, unknown>) => Promise<T>,
): Promise<T> {
  let current = { ...payload };

  // One attempt per potentially missing column, plus the successful one
  for (let attempt = 0; attempt < 6; attempt++) {
    try {
      return await run(current);
    } catch (error) {
      const column = getMissingColumn(error);
      if (!column || !(column in current)) throw error;
      delete current[column];
      reportMissingColumn(column);
    }
  }
  return run(current);
}
